import { useLocalStorage } from "@medoma/shared-employee/useLocalStorage";
import { useUserProfile } from "@/api/Users";
import { z } from "zod";

export const mapPositionSchema = z.enum(["right", "bottom"]);
type MapPosition = z.infer<typeof mapPositionSchema>;

export const viewModeSchema = z.enum(["timeline", "list"]);
type ViewMode = z.infer<typeof viewModeSchema>;

export const usePlanningViewToolbar = () => {
  const { userId } = useUserProfile();

  const [storedExpandedValue, setStoredExpandedValue] = useLocalStorage({
    key: "planning.viewExpanded",
    userId,
    initialValue: "false",
  });
  const [storedMapShownValue, setStoredMapShownValue] = useLocalStorage({
    key: "planning.mapShown",
    userId,
    initialValue: "false",
  });
  const [storedMapPositionValue, setStoredMapPositionValue] = useLocalStorage({
    key: "planning.mapPosition",
    userId,
    initialValue: mapPositionSchema.enum.right,
  });

  const [storedViewModeValue, setStoredViewModeValue] = useLocalStorage({
    key: "planning.viewMode",
    userId,
    initialValue: viewModeSchema.enum.timeline,
  });

  const isExpanded = storedExpandedValue === "true";

  const setIsExpanded = (value: boolean) => {
    setStoredExpandedValue(value ? "true" : "false");
  };

  const isMapShown = storedMapShownValue === "true";

  const setMapShown = (value: boolean) => {
    setStoredMapShownValue(value ? "true" : "false");
  };

  const maybeMapPosition = mapPositionSchema.safeParse(storedMapPositionValue);
  const mapPosition = maybeMapPosition.success
    ? maybeMapPosition.data
    : mapPositionSchema.enum.right;

  const setMapPosition = (value: MapPosition) => {
    setStoredMapPositionValue(value);
  };

  const parsedViewMode = viewModeSchema.safeParse(storedViewModeValue);
  const viewMode = parsedViewMode.success
    ? parsedViewMode.data
    : viewModeSchema.enum.timeline;

  const setViewMode = (value: ViewMode) => {
    setStoredViewModeValue(value);
  };

  return {
    isExpanded,
    setIsExpanded,
    isMapShown,
    setMapShown,
    mapPosition,
    setMapPosition,
    viewMode,
    setViewMode,
  };
};
